import {
  create_award_form,
  create_bid_form,
  create_category_form,
  create_deviation_form,
  create_irregularity_form,
  create_requisition_drawer_from_row,
  create_requisition_form,
  create_rule_form,
  currency_from_number,
  eligibility_badge_class,
  irregularity_badge_class,
  irregularity_icon_class,
  number_from_currency,
  requisition_status_class
} from './procurement_compliance_helpers';
import {
  active_procurement_overlay,
  award_form_from_row,
  award_status_class,
  bid_form_from_row,
  declarations_class,
  deviation_form_from_row,
  irregularity_form_from_row,
  modal_row_id,
  procurement_close_handler,
  replace_or_prepend,
  requisition_form_from_row,
  status_class
} from './procurement_compliance_ui_helpers';
import { category_row_payload } from './procurement_ui_row_helpers';

function threshold_row_note(approver, evidence_value) {
  const note = `${approver || 'Approver'} - ${evidence_value || 'Evidence required'}`;
  return note;
}

function threshold_row_payload(context) {
  const value_band = context.threshold_form.value_band.trim();
  const required_method = context.threshold_form.required_method.trim();
  if (!value_band || !required_method) return null;
  const approver = context.threshold_form.approver.trim();
  const evidence_value = context.threshold_form.required_evidence.trim();
  const note = threshold_row_note(approver, evidence_value);
  const row_payload = { limit: value_band, title: required_method, note, approver, evidence: evidence_value };
  return row_payload;
}

function requisition_row_payload(context) {
  const requisition = context.requisition_form.requisition.trim();
  if (!requisition) return null;
  const row_payload = {
    id: modal_row_id(context.requisition_modal_mode, context.editing_requisition_key, context.requisition_rows, 'REQ-'),
    requisition,
    dept: context.requisition_form.department.trim() || 'Unassigned',
    value: currency_from_number(number_from_currency(context.requisition_form.value)),
    threshold: context.requisition_form.threshold_band,
    method: context.requisition_form.procurement_method,
    stage: context.requisition_form.stage.trim(),
    supplier: context.requisition_form.selected_supplier.trim() || '-',
    note: context.requisition_form.note.trim(),
    eligibility: { label: context.requisition_form.supplier_eligibility, class: eligibility_badge_class(context.requisition_form.supplier_eligibility) },
    status: { label: context.requisition_form.compliance_status, class: requisition_status_class(context.requisition_form.compliance_status) },
    highlighted: context.requisition_modal_mode === 'create'
  };
  row_payload.drawer = create_requisition_drawer_from_row(row_payload);
  return row_payload;
}

function bid_row_payload(context) {
  const requisition = context.bid_form.requisition.trim();
  if (!requisition) return null;
  const declarations = context.bid_form.declarations.trim();
  const row_payload = {
    id: modal_row_id(context.bid_modal_mode, context.editing_bid_key, context.bid_rows, 'BID-'),
    requisition,
    committee: context.bid_form.committee,
    chairperson: context.bid_form.chairperson.trim(),
    declarations: { label: declarations || 'Complete', class: declarations_class(declarations, context.bid_form.status) },
    evaluation: context.bid_form.evaluation.trim(),
    recommendation: context.bid_form.recommendation.trim(),
    status: { label: context.bid_form.status, class: status_class(context.bid_form.status, 'Approved', ['In progress', 'Under review']) },
    highlighted: context.bid_modal_mode === 'create'
  };
  return row_payload;
}

function award_row_payload(context) {
  const supplier = context.award_form.supplier.trim();
  if (!supplier) return null;
  const award_status = context.award_form.status;
  const row_payload = {
    id: modal_row_id(context.award_modal_mode, context.editing_award_key, context.award_rows, 'AWD-'),
    supplier,
    for: context.award_form.requisition.trim(),
    value: currency_from_number(number_from_currency(context.award_form.value)),
    method: context.award_form.method,
    approved_by: context.award_form.approved_by.trim() || '-',
    date: context.award_form.date.trim(),
    status: { label: award_status, class: award_status_class(award_status) },
    highlighted: context.award_modal_mode === 'create'
  };
  return row_payload;
}

function deviation_row_payload(context) {
  const title = context.deviation_form.title.trim();
  if (!title) return null;
  const all_rows = [...context.deviation_rows, ...context.emergency_rows];
  const row_payload = {
    id: modal_row_id(context.deviation_modal_mode, context.editing_deviation_key, all_rows, 'DEV-'),
    title,
    type: context.deviation_form.type,
    value: currency_from_number(number_from_currency(context.deviation_form.value)),
    justification: context.deviation_form.justification.trim(),
    approved_by: context.deviation_form.approved_by.trim(),
    date: context.deviation_form.date.trim(),
    status: { label: context.deviation_form.status, class: status_class(context.deviation_form.status, 'Approved', ['Under review', 'Condonement sought']) },
    highlighted: context.deviation_modal_mode === 'create'
  };
  return row_payload;
}

function irregularity_row_payload(context) {
  const flag = context.irregularity_form.flag.trim();
  if (!flag) return null;
  const row_payload = {
    id: modal_row_id(context.irregularity_modal_mode, context.editing_irregularity_key, context.irregularity_rows, 'IRR-', 2),
    title: flag,
    meta: `${context.irregularity_form.type} - ${context.irregularity_form.detail.trim()}`,
    type: context.irregularity_form.type,
    detail: context.irregularity_form.detail.trim(),
    severity: context.irregularity_form.severity,
    status: context.irregularity_form.status,
    badge: context.irregularity_form.severity,
    badge_class: irregularity_badge_class(context.irregularity_form.severity),
    value: currency_from_number(number_from_currency(context.irregularity_form.value)),
    icon: irregularity_icon_class(context.irregularity_form.type),
    highlighted: context.irregularity_modal_mode === 'create'
  };
  return row_payload;
}

export const procurement_computed = {
  procurement_modal_overlay_open() {
    const overlay_open = active_procurement_overlay(this);
    return overlay_open;
  },
  category_modal_title() { return this.category_modal_mode === 'edit' ? 'Edit plan category' : 'New plan category'; },
  category_modal_submit_label() { return this.category_modal_mode === 'edit' ? 'Save changes' : 'Create'; },
  threshold_modal_title() { return this.threshold_modal_mode === 'edit' ? 'Edit threshold rule' : 'New threshold rule'; },
  threshold_modal_submit_label() { return this.threshold_modal_mode === 'edit' ? 'Save changes' : 'Create'; },
  procurement_modal_close_handler() {
    const close_handler = procurement_close_handler(this);
    return close_handler;
  }
};

export const procurement_watch = {
  procurement_modal_overlay_open: {
    immediate: true,
    handler(is_open) {
      if (typeof document !== 'undefined') document.body.classList.toggle('procurement-overlay-open', is_open);
    }
  }
};

export function procurement_before_unmount() {
  if (typeof document !== 'undefined') document.body.classList.remove('procurement-overlay-open');
}

export const procurement_methods = {
  set_tab(tab_id) {
    this.active_tab = tab_id;
    this.close_category_modal(); this.close_threshold_modal(); this.close_requisition_modal();
    this.close_bid_modal(); this.close_award_modal(); this.close_deviation_modal();
    this.close_irregularity_modal(); this.close_requisition_drawer();
  },
  open_category_modal() { this.close_threshold_modal(); this.category_modal_mode = 'create'; this.editing_category_key = null; this.category_form = create_category_form(); this.category_modal_open = true; },
  open_edit_category_modal(row) { this.close_threshold_modal(); this.category_modal_mode = 'edit'; this.editing_category_key = row.category; this.category_form = { category: row.category, planned_budget: String(number_from_currency(row.planned)), committed_to_date: String(number_from_currency(row.committed)), status: row.status.label }; this.category_modal_open = true; },
  close_category_modal() { this.category_modal_open = false; this.editing_category_key = null; this.category_form = create_category_form(); },
  submit_category_modal() {
    const row_payload = category_row_payload(this);
    if (!row_payload) return;
    this.plan_rows = this.category_modal_mode === 'edit' ? this.plan_rows.map((row) => row.category === this.editing_category_key ? row_payload : row) : [row_payload, ...this.plan_rows];
    this.close_category_modal();
  },
  open_threshold_modal() { this.close_category_modal(); this.threshold_modal_mode = 'create'; this.editing_threshold_key = null; this.threshold_form = create_rule_form(); this.threshold_modal_open = true; },
  open_edit_threshold_modal(row) { this.close_category_modal(); this.threshold_modal_mode = 'edit'; this.editing_threshold_key = row.limit; this.threshold_form = { value_band: row.limit, required_method: row.title, approver: row.approver || '', required_evidence: row.evidence || '' }; this.threshold_modal_open = true; },
  close_threshold_modal() { this.threshold_modal_open = false; this.editing_threshold_key = null; this.threshold_form = create_rule_form(); },
  submit_threshold_modal() {
    const row_payload = threshold_row_payload(this);
    if (!row_payload) return;
    this.threshold_rows = this.threshold_modal_mode === 'edit' ? this.threshold_rows.map((row) => row.limit === this.editing_threshold_key ? row_payload : row) : [row_payload, ...this.threshold_rows];
    this.close_threshold_modal();
  },
  delete_threshold_rule() {
    if (this.threshold_modal_mode === 'edit') this.threshold_rows = this.threshold_rows.filter((row) => row.limit !== this.editing_threshold_key);
    this.close_threshold_modal();
  },
  open_requisition_modal() { this.close_requisition_drawer(); this.requisition_modal_mode = 'create'; this.editing_requisition_key = null; this.requisition_form = create_requisition_form(); this.requisition_modal_open = true; },
  open_edit_requisition_modal(row) {
    this.close_requisition_drawer(); this.requisition_modal_mode = 'edit'; this.editing_requisition_key = row.id;
    this.requisition_form = requisition_form_from_row(row);
    this.requisition_modal_open = true;
  },
  close_requisition_modal() { this.requisition_modal_open = false; this.requisition_modal_mode = 'create'; this.editing_requisition_key = null; this.requisition_form = create_requisition_form(); },
  open_requisition_drawer(row) { this.active_requisition_drawer = create_requisition_drawer_from_row(row); },
  close_requisition_drawer() { this.active_requisition_drawer = null; },
  submit_requisition_modal() {
    const row_payload = requisition_row_payload(this);
    if (!row_payload) return;
    if (this.requisition_modal_mode === 'edit') {
      row_payload.highlighted = false;
      this.requisition_rows = this.requisition_rows.map((row) => row.id === this.editing_requisition_key ? row_payload : row);
      this.active_requisition_drawer = row_payload.drawer;
    } else this.requisition_rows.unshift(row_payload);
    this.close_requisition_modal();
  },
  open_bid_modal() { this.close_award_modal(); this.bid_modal_mode = 'create'; this.editing_bid_key = null; this.bid_form = create_bid_form(); this.bid_modal_open = true; },
  open_edit_bid_modal(row) {
    this.close_award_modal(); this.bid_modal_mode = 'edit'; this.editing_bid_key = row.id;
    this.bid_form = bid_form_from_row(row);
    this.bid_modal_open = true;
  },
  close_bid_modal() { this.bid_modal_open = false; this.bid_modal_mode = 'create'; this.editing_bid_key = null; this.bid_form = create_bid_form(); },
  submit_bid_modal() {
    const row_payload = bid_row_payload(this);
    if (!row_payload) return;
    this.bid_rows = replace_or_prepend(this.bid_rows, this.editing_bid_key, row_payload, this.bid_modal_mode === 'edit');
    this.close_bid_modal();
  },
  delete_bid_modal() { if (this.editing_bid_key) this.bid_rows = this.bid_rows.filter((row) => row.id !== this.editing_bid_key); this.close_bid_modal(); },
  open_award_modal() { this.close_bid_modal(); this.award_modal_mode = 'create'; this.editing_award_key = null; this.award_form = create_award_form(); this.award_modal_open = true; },
  open_edit_award_modal(row) {
    this.close_bid_modal(); this.award_modal_mode = 'edit'; this.editing_award_key = row.id;
    this.award_form = award_form_from_row(row);
    this.award_modal_open = true;
  },
  close_award_modal() { this.award_modal_open = false; this.award_modal_mode = 'create'; this.editing_award_key = null; this.award_form = create_award_form(); },
  submit_award_modal() {
    const row_payload = award_row_payload(this);
    if (!row_payload) return;
    this.award_rows = replace_or_prepend(this.award_rows, this.editing_award_key, row_payload, this.award_modal_mode === 'edit');
    this.close_award_modal();
  },
  delete_award_modal() { if (this.editing_award_key) this.award_rows = this.award_rows.filter((row) => row.id !== this.editing_award_key); this.close_award_modal(); },
  open_deviation_modal(type = 'Deviation') { this.deviation_modal_mode = 'create'; this.editing_deviation_key = null; this.editing_deviation_source = type === 'Emergency' ? 'emergency' : 'deviation'; this.deviation_form = { ...create_deviation_form(), type }; this.deviation_modal_open = true; },
  open_edit_deviation_modal(row, source = 'deviation') {
    this.deviation_modal_mode = 'edit'; this.editing_deviation_key = row.id; this.editing_deviation_source = source;
    this.deviation_form = deviation_form_from_row(row, source);
    this.deviation_modal_open = true;
  },
  close_deviation_modal() { this.deviation_modal_open = false; this.deviation_modal_mode = 'create'; this.editing_deviation_key = null; this.editing_deviation_source = 'deviation'; this.deviation_form = create_deviation_form(); },
  submit_deviation_modal() {
    const row_payload = deviation_row_payload(this);
    if (!row_payload) return;
    this.deviation_rows = this.deviation_rows.filter((row) => row.id !== this.editing_deviation_key);
    this.emergency_rows = this.emergency_rows.filter((row) => row.id !== this.editing_deviation_key);
    (this.deviation_form.type === 'Emergency' ? this.emergency_rows : this.deviation_rows).unshift(this.deviation_modal_mode === 'edit' ? { ...row_payload, id: this.editing_deviation_key, highlighted: false } : row_payload);
    this.close_deviation_modal();
  },
  delete_deviation_modal() { if (this.editing_deviation_key) { this.deviation_rows = this.deviation_rows.filter((row) => row.id !== this.editing_deviation_key); this.emergency_rows = this.emergency_rows.filter((row) => row.id !== this.editing_deviation_key); } this.close_deviation_modal(); },
  open_irregularity_modal() { this.irregularity_modal_mode = 'create'; this.editing_irregularity_key = null; this.irregularity_form = create_irregularity_form(); this.irregularity_modal_open = true; },
  open_edit_irregularity_modal(item) {
    this.irregularity_modal_mode = 'edit'; this.editing_irregularity_key = item.id;
    this.irregularity_form = irregularity_form_from_row(item);
    this.irregularity_modal_open = true;
  },
  close_irregularity_modal() { this.irregularity_modal_open = false; this.irregularity_modal_mode = 'create'; this.editing_irregularity_key = null; this.irregularity_form = create_irregularity_form(); },
  submit_irregularity_modal() {
    const row_payload = irregularity_row_payload(this);
    if (!row_payload) return;
    this.irregularity_rows = replace_or_prepend(this.irregularity_rows, this.editing_irregularity_key, row_payload, this.irregularity_modal_mode === 'edit');
    this.close_irregularity_modal();
  },
  delete_irregularity_modal() { if (this.editing_irregularity_key) this.irregularity_rows = this.irregularity_rows.filter((row) => row.id !== this.editing_irregularity_key); this.close_irregularity_modal(); },
  export_requisition_evidence() { if (!this.active_requisition_drawer) return; }
};
